// @ts-nocheck
import { grey } from '@mui/material/colors'
import {
  type PaletteColor,
  type ThemeOptions,
  type Typography,
  type TypographyOptions,
  type TypographyVariantsOptions,
  createTheme,
} from '@mui/material/styles'

declare module '@mui/material/styles' {
  export interface CustomPaletteColor extends PaletteColor {
    states: {
      hover: string
      selected: string
      focus: string
      outlinedBorder?: string
    }
  }

  interface TypeBackground {
    primary: string
    secondary: string
  }

  interface Palette {
    primary: CustomPaletteColor
    secondary: CustomPaletteColor
    error: CustomPaletteColor
    warning: CustomPaletteColor
    success: CustomPaletteColor
    text: {
      primary: string
      secondary: string
      disabled: string
      states: {
        hover: string
        selected: string
        focus: string
      }
    }
    background: TypeBackground
    action: {
      active: string
      hover: string
      selected: string
      focus: string
      disabled: string
      disabledInput: string
      disabledBackground: string
    }
    input: {
      default: string
      hover: string
      selected: string
      focus: string
      disabled: string
    }
    aiSearch: {
      border: string
      hover: string
      background: string
      searchBackground: string
      tabBackground: string
      selectedTabBackground: string
      tabText: string
    }
    processFlow: {
      border: {
        default: string
        hover: string
        applicant: string
        applicantHover: string
        internal: string
        internalHover: string
        bill: string
        billHover: string
        communication: string
        communicationHover: string
        focus: string
        error: string
      }
      background: {
        default: string
        dark: string
        applicant: string
        internal: string
        approved: string
        open: string
        unsubmitted: string
        deleted: string
        hidden: string
      }
      button: {
        primary: string
        secondary: string
      }
      edge: {
        default: string
      }
      menu: {
        background: {
          primary: string
          secondary: string
          hover: string
          disabled: string
        }
        text: {
          primary: string
          secondary: string
          disabled: string
          link: string
          linkHover: string
        }
        divider: {
          main: string
        }
        error: {
          main: string
        }
      }
    }
    purple: PaletteColor
    berry: PaletteColor
    brown: PaletteColor
    bayleaf: PaletteColor
    glacier: PaletteColor
    teal: PaletteColor
    magenta: PaletteColor
    gold: PaletteColor
    indigo: PaletteColor
    grape: PaletteColor
    spearmint: PaletteColor
    walnut: PaletteColor
    cinnamon: PaletteColor
    periwinkle: PaletteColor
    sky: PaletteColor
    slate: PaletteColor
    fog: PaletteColor
    blue: PaletteColor
    green: PaletteColor
    red: PaletteColor
    orange: PaletteColor
  }

  interface PaletteOptions {
    primary?: Partial<CustomPaletteColor>
    secondary?: Partial<CustomPaletteColor>
    error?: Partial<CustomPaletteColor>
    warning?: Partial<CustomPaletteColor>
    success?: Partial<CustomPaletteColor>
    text?: Partial<Palette['text']>
    background?: Partial<Palette['background']>
    action?: Partial<Palette['action']>
    input?: Partial<Palette['input']>
    aiSearch?: Partial<Palette['aiSearch']>
    processFlow?: Partial<Palette['processFlow']>
    purple?: Partial<PaletteColor>
    berry?: Partial<PaletteColor>
    brown?: Partial<PaletteColor>
    bayleaf?: Partial<PaletteColor>
    glacier?: Partial<PaletteColor>
    teal?: Partial<PaletteColor>
    magenta?: Partial<PaletteColor>
    gold?: Partial<PaletteColor>
    indigo?: Partial<PaletteColor>
    grape?: Partial<PaletteColor>
    spearmint?: Partial<PaletteColor>
    walnut?: Partial<PaletteColor>
    cinnamon?: Partial<PaletteColor>
    periwinkle?: Partial<PaletteColor>
    sky?: Partial<PaletteColor>
    slate?: Partial<PaletteColor>
    fog?: Partial<PaletteColor>
    blue?: Partial<PaletteColor>
    green?: Partial<PaletteColor>
    red?: Partial<PaletteColor>
    orange?: Partial<PaletteColor>
  }

  interface TypographyVariants {
    label: React.CSSProperties
    link: React.CSSProperties
  }

  interface TypographyVariantsOptions {
    label?: React.CSSProperties
    link?: React.CSSProperties
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    label: true
    link: true
  }
}

declare module '@mui/material/SvgIcon' {
  interface SvgIconPropsColorOverrides {
    grape: true
  }
}

const createCustomTheme = (mode: 'light' | 'dark'): ThemeOptions => {
  const typography: TypographyVariantsOptions = {
    fontFamily: 'avenir-book, sans-serif',
    h1: {
      fontFamily: 'avenir-book, sans-serif',
      fontSize: '28px',
      lineHeight: '125%',
    },
    h2: {
      fontFamily: 'avenir-book, sans-serif',
      fontSize: '24px',
      letterSpacing: '1%',
      lineHeight: '125%',
    },
    h3: {
      fontFamily: 'avenir-book, sans-serif',
      fontSize: '20px',
      letterSpacing: '1%',
      lineHeight: '150%',
    },
    h4: {
      fontFamily: 'avenir-book, sans-serif',
      fontSize: '18px',
      letterSpacing: '1%',
      lineHeight: '150%',
    },
    h5: {
      fontFamily: 'avenir-heavy, sans-serif',
      fontSize: '16px',
      letterSpacing: '2%',
      lineHeight: '24px',
    },
    body1: {
      fontFamily: 'avenir-book, sans-serif',
      fontSize: '16px',
      letterSpacing: '0.25px',
      lineHeight: '24px',
    },
    body2: {
      fontFamily: 'avenir-book, sans-serif',
      fontSize: '14px',
      letterSpacing: '0.25px',
      lineHeight: '24px',
    },
    button: {
      fontFamily: 'avenir-light, sans-serif',
      fontSize: '16px',
      fontWeight: 700,
      letterSpacing: '0.5px',
      lineHeight: '24px',
      textTransform: 'none',
    },
    subtitle1: {
      fontFamily: 'avenir-medium, sans-serif',
      fontSize: '24px',
      letterSpacing: '0.2px',
      lineHeight: '150%',
    },
    subtitle2: {
      fontFamily: 'avenir-medium, sans-serif',
      fontSize: '20px',
      letterSpacing: '0.2px',
      lineHeight: '150%',
    },
    caption: {
      fontFamily: 'avenir-book, sans-serif',
      fontSize: '12px',
      letterSpacing: '0.4px',
    },
    overline: {
      fontFamily: 'avenir-book, sans-serif',
      fontSize: '10px',
      letterSpacing: '1px',
    },
    label: {
      fontFamily: 'avenir-book, sans-serif',
      fontSize: '14px',
      letterSpacing: '0.25px',
      lineHeight: '18px',
      color: mode === 'light' ? '#666666' : '#FFFFFFB3',
    },
    link: {
      fontFamily: 'avenir-heavy, sans-serif',
      fontSize: '16px',
      letterSpacing: '0.25px',
      lineHeight: '24px',
      color: mode === 'light' ? '#067EB1' : '#90CAF9',
    },
  }

  return {
    typography,
    components: {
      MuiSvgIcon: {
        styleOverrides: {
          root: {
            '& path': {
              strokeWidth: 0.5,
            },
          },
        },
      },
    },
    palette: {
      mode,
      purple: {
        main: '#DE7DA5',
      },
      berry: {
        main: '#960191',
      },
      brown: {
        main: '#BD946C',
      },
      bayleaf: {
        main: '#465E50',
      },
      glacier: {
        main: '#DFF5F3',
      },
      teal: {
        main: '#00A296',
      },
      magenta: {
        main: '#D73DB1',
      },
      gold: {
        main: '#C1A422',
      },
      indigo: {
        main: '#3A68E0',
      },
      grape: {
        main: '#5E26BF',
      },
      spearmint: {
        main: '#7CDEA6',
      },
      walnut: {
        main: '#615B3C',
      },
      cinnamon: {
        main: '#C45C00',
      },
      periwinkle: {
        main: '#8587FB',
      },
      sky: {
        main: '#79CAE3',
      },
      slate: {
        main: '#535965',
      },
      fog: {
        main: '#EFF5FC',
      },
      blue: {
        main: '#0894CF',
      },
      green: {
        main: '#009C6D',
      },
      red: {
        main: '#ED1C24',
      },
      orange: {
        main: '#FF6E3C',
      },
      ...(mode === 'light'
        ? {
            primary: {
              main: '#067EB1',
              dark: '#0070A8',
              light: '#0FA1DD',
              contrastText: '#FFFFFF',
              states: {
                hover: '#067EB10A',
                selected: '#067EB114',
                focus: '#98e0ff',
                outlinedBorder: '#067EB180',
              },
            },
            secondary: {
              main: '#000000',
              dark: '#4A4A4A',
              light: '#888888',
              contrastText: '#FFFFFF',
              states: {
                hover: '#00000009',
                selected: '#0000001A',
                focus: '#98e0ff',
                outlinedBorder: '#00000080',
              },
            },
            error: {
              main: '#ED1C24',
              dark: '#CE0015',
              light: '#FC2B22',
              contrastText: '#FFFFFF',
              states: {
                hover: '#ED1C240A',
                selected: '#ED1C2414',
                focus: '#98e0ff',
                outlinedBorder: '#ED1C2480',
              },
            },
            warning: {
              main: '#FF6E3C',
              dark: '#E6460D',
              light: '#FF8760',
              contrastText: '#FFFFFF',
              states: {
                hover: '#FF6E3C0A',
                selected: '#FF6E3C14',
                focus: '#FF6E3C1F',
                outlinedBorder: '#FF6E3C80',
              },
            },
            success: {
              main: '#009C6D',
              dark: '#007F53',
              light: '#2AAB80',
              contrastText: '#FFFFFF',
              states: {
                hover: '#009C6D0A',
                selected: '#009C6D14',
                focus: '#98e0ff',
                outlinedBorder: '#009C6D80',
              },
            },
            text: {
              primary: '#000000',
              secondary: '#666666', // titanium
              disabled: '#888888', // carbon
              states: {
                hover: '#0000000A',
                selected: '#00000014',
                focus: '#98e0ff',
              },
            },
            background: {
              primary: '#FFFFFF',
              secondary: '#F8F8F8',
            },
            action: {
              active: '#0000008F',
              hover: '#0000000A',
              selected: '#00000014',
              focus: '#98e0ff',
              disabled: '#00000061',
              disabledInput: '#F8F8F8',
              disabledBackground: '#F8F8F8',
            },
            input: {
              default: '#666666',
              hover: '#0070A8',
              focus: '#98E0FF',
              error: '#ED1C24',
              disabled: '#888888',
            },
            aiSearch: {
              border: '#F0F0F0',
              hover: '#f5f5f5',
              background: 'transparent',
              searchBackground: '#FFFFFF',
              tabBackground: 'rgb(230, 244, 250)',
              selectedTabBackground: '#F2F2F3',
              tabText: '#000000',
            },
            processFlow: {
              border: {
                default: '#666666',
                hover: '#2A2A2A',
                applicant: '#E75F48',
                applicantHover: '#C43921',
                internal: '#C1A422',
                internalHover: '#997D00',
                bill: '#009C6D',
                billHover: '#006F4E',
                communication: '#582481',
                communicationHover: '#48146f',
                focus: '#0070A8',
                error: '#F8C4C44D',
              },
              background: {
                default: '#D9D9D9',
                dark: '#626A7C',
                applicant: '#FDDBD4',
                internal: '#FBF4D4',
                approved: '#D8F5EC',
                open: '#E1F2F9',
                unsubmitted: '#F7E0E9',
                deleted: '#E0E0E0',
                hidden: '#EFEFEF',
              },
              button: {
                primary: '#0894CF',
                secondary: '#000000',
              },
              edge: {
                default: grey[300],
              },
              menu: {
                background: {
                  primary: '#FFFFFF',
                  secondary: '#EFEFF2',
                  hover: '#E6EEF6',
                  disabled: '#9595A4',
                },
                text: {
                  primary: '#010B34',
                  secondary: '#18181B',
                  disabled: '#666666',
                  link: '#E75F48',
                  linkHover: '#C43921',
                },
                divider: {
                  main: '#00000012',
                },
                error: {
                  main: '#8F0202',
                },
              },
            },
          }
        : {
            primary: {
              main: '#067EB1',
              dark: 'blue.400',
              light: 'blue.50',
              contrastText: '#000000DE',
              states: {
                hover: '#90CAF914',
                selected: '#90CAF929',
                focus: '#98e0ff',
                outlinedBorder: '#90CAF980',
              },
            },
            secondary: {
              main: 'purple',
              dark: '#FFFFFF',
              light: 'purple.50',
              contrastText: '#000000DE',
              states: {
                hover: '#CE93D814',
                selected: '#CE93D829',
                focus: '#CE93D81F',
                outlinedBorder: '#CE93D880',
              },
            },
            error: {
              main: 'red.500',
              dark: 'red.700',
              light: 'red.300',
              contrastText: '#FFFFFF',
              states: {
                hover: '#F4433614',
                selected: '#F4433629',
                focus: '#F4433621',
                outlinedBorder: '#F4433680',
              },
            },
            warning: {
              main: 'orange.400',
              dark: 'orange.700',
              light: 'orange.300',
              contrastText: '#000000DE',
              states: {
                hover: '#FFA72614',
                selected: '#FFA72629',
                focus: '#FFA72621',
                outlinedBorder: '#FFA72680',
              },
            },
            success: {
              main: 'green.400',
              dark: 'green.700',
              light: 'green.300',
              contrastText: '#000000DE',
              states: {
                hover: '#66BB6A14',
                selected: '#66BB6A29',
                focus: '#66BB6A21',
                outlinedBorder: '#66BB6A80',
              },
            },
            text: {
              primary: '#FFFFFF',
              secondary: '#FFFFFFB3', // 70% opacity
              disabled: '#FFFFFF61', // 38% opacity
              states: {
                hover: '#FFFFFF14',
                selected: '#FFFFFF29',
                focus: '#FFFFFF1F',
              },
            },
            background: {
              primary: '#000000DE',
              secondary: '#888888',
            },
            action: {
              active: '#FFFFFF8F',
              hover: '#FFFFFF14',
              selected: '#FFFFFF29',
              focus: '#FFFFFF1F',
              disabled: '#FFFFFF61',
              disabledInput: '#F0F0F0',
              disabledBackground: '#F8F8F8',
            },
            input: {
              default: '#666666',
              hover: '#067EB1',
              focus: '#98E0FF',
              error: '#ED1C24',
              disabled: '#888888',
            },
            aiSearch: {
              border: '#000000',
              hover: '#FFFFFF24',
              background: 'var(--carbon)',
              searchBackground: 'var(--lightcarbon)',
              tabBackground: '#067EB1',
              selectedTabBackground: '#FFFFFF24',
              tabText: '#FFFFFF',
            },
            processFlow: {
              border: {
                default: '#666666',
                hover: '#2A2A2A',
                applicant: '#E75F48',
                applicantHover: '#C43921',
                internal: '#C1A422',
                internalHover: '#997D00',
                bill: '#009C6D',
                billHover: '#006F4E',
                communication: '#582481',
                communicationHover: '#48146f',
                focus: '#0894CF',
                error: '#F8C4C44D',
              },
              background: {
                default: '#D9D9D9',
                dark: '#626A7C',
                applicant: '#FDDBD4',
                internal: '#FBF4D4',
                approved: '#D8F5EC',
                open: '#E1F2F9',
                unsubmitted: '#F7E0E9',
                deleted: '#E0E0E0',
                hidden: '#EFEFEF',
              },
              button: {
                primary: '#0894CF',
                secondary: '#000000',
              },
              edge: {
                default: grey[300],
              },
              menu: {
                background: {
                  primary: '#FFFFFF',
                  secondary: '#EFEFF2',
                  hover: '#E6EEF6',
                },
                text: {
                  primary: '#010B34',
                  secondary: '#18181B',
                  disabled: '#666666',
                  link: '#E75F48',
                  linkHover: '#C43921',
                },
                divider: {
                  main: '#00000012',
                },
                error: {
                  main: '#BF2626',
                },
              },
            },
          }),
    },
  }
}

export const lightTheme = createTheme(createCustomTheme('light'))
export const darkTheme = createTheme(createCustomTheme('dark'))

// Ensure type safety
declare module '@mui/material/styles' {
  interface Theme {
    typography: Typography
  }
  interface ThemeOptions {
    typography?: TypographyOptions | ((palette: Palette) => TypographyOptions)
  }
}
